// Sepia's hand-made UI kit: sketchy paper panels, washi tape, marker-cap swatches, wavy sliders.
import rough from "roughjs";
import {
  createContext, forwardRef, useCallback, useContext, useEffect, useId, useLayoutEffect, useMemo, useRef, useState,
  type CSSProperties, type ReactNode
} from "react";
import { createPortal } from "react-dom";
import type { LucideIcon } from "lucide-react";

const gen = rough.generator();

function hashSeed(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  return (h >>> 0) % 2147483646 + 1;
}

/** A hand-inked outline that follows the size of its parent. */
export function SketchOutline({ seed = "box", rough: r = 0.9, stroke = "var(--line)", width = 1.7, inset = 1.5, fill }: { seed?: string; rough?: number; stroke?: string; width?: number; inset?: number; fill?: string }) {
  const ref = useRef<SVGSVGElement>(null);
  const [size, setSize] = useState<{ w: number; h: number } | null>(null);
  useLayoutEffect(() => {
    const node = ref.current?.parentElement;
    if (!node) return;
    const measure = () => setSize((s) => {
      const w = Math.round(node.offsetWidth);
      const h = Math.round(node.offsetHeight);
      return s && s.w === w && s.h === h ? s : { w, h };
    });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(node);
    return () => ro.disconnect();
  }, []);
  const paths = useMemo(() => {
    if (!size || size.w < 4 || size.h < 4) return [];
    const d = gen.rectangle(inset, inset, size.w - inset * 2, size.h - inset * 2, {
      seed: hashSeed(seed), roughness: r, bowing: 1.1, strokeWidth: width, stroke: "#000", fill: fill ? "#000" : undefined, fillStyle: "solid"
    });
    return gen.toPaths(d);
  }, [size, seed, r, width, inset, fill]);
  return (
    <svg ref={ref} className="sketch-outline" aria-hidden="true" width={size?.w ?? 0} height={size?.h ?? 0}>
      {paths.map((p, i) => (
        <path key={i} d={p.d} fill={p.fill && p.fill !== "none" ? fill : "none"} stroke={p.stroke === "none" ? "none" : stroke} strokeWidth={p.strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
      ))}
    </svg>
  );
}

export function Tape({ color = "var(--rose)", className = "", style }: { color?: string; className?: string; style?: CSSProperties }) {
  return <span className={"ui-tape " + className} style={{ background: color, ...style }} aria-hidden="true" />;
}

/** A paper panel with an inked outline, optionally taped to the screen. */
export const Panel = forwardRef<HTMLDivElement, { children: ReactNode; className?: string; seed?: string; tape?: string | null; tapeSide?: "left" | "right" | "center"; style?: CSSProperties; role?: string; "aria-label"?: string }>(
  function Panel({ children, className = "", seed = "panel", tape = null, tapeSide = "left", style, ...rest }, ref) {
    return (
      <div ref={ref} className={"panel " + className} style={style} {...rest}>
        <SketchOutline seed={seed} />
        {tape && <Tape color={tape} className={"panel-tape tape-" + tapeSide} />}
        {children}
      </div>
    );
  }
);

export function Icon({ icon: I, size = 20, className }: { icon: LucideIcon; size?: number; className?: string }) {
  return <I size={size} strokeWidth={1.9} className={"ico " + (className ?? "")} aria-hidden="true" />;
}

export const IconButton = forwardRef<HTMLButtonElement, {
  icon: LucideIcon; label: string; onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void; active?: boolean; disabled?: boolean;
  size?: number; className?: string; badge?: boolean; shortcut?: string; tipSide?: "right" | "left" | "top" | "bottom";
}>(function IconButton({ icon, label, onClick, active, disabled, size = 21, className = "", badge, shortcut, tipSide = "bottom" }, ref) {
  return (
    <button
      ref={ref}
      type="button"
      className={"icon-btn " + (active ? "active " : "") + className}
      aria-label={label}
      aria-pressed={active}
      data-tip={shortcut ? `${label} · ${shortcut}` : label}
      data-tip-side={tipSide}
      disabled={disabled}
      onClick={onClick}
    >
      <Icon icon={icon} size={size} />
      {badge && <span className="icon-badge" aria-hidden="true" />}
    </button>
  );
});

export function Button({ children, onClick, kind = "plain", disabled, type = "button", className = "", title }: { children: ReactNode; onClick?: () => void; kind?: "plain" | "primary" | "ghost" | "danger"; disabled?: boolean; type?: "button" | "submit"; className?: string; title?: string }) {
  return (
    <button type={type} className={`btn btn-${kind} ${className}`} onClick={onClick} disabled={disabled} title={title}>
      {children}
    </button>
  );
}

export function Chip({ children, active, onClick, title, className = "", style }: { children: ReactNode; active?: boolean; onClick?: () => void; title?: string; className?: string; style?: CSSProperties }) {
  return (
    <button type="button" className={"chip " + (active ? "active " : "") + className} onClick={onClick} title={title} aria-pressed={active} style={style}>
      {children}
    </button>
  );
}

/** A marker drawn as its cap: the colour choice of the ink palette. */
export function MarkerSwatch({ color, active, onClick, label }: { color: string; active?: boolean; onClick?: () => void; label: string }) {
  const light = isLight(color);
  return (
    <button type="button" className={"marker" + (active ? " active" : "")} onClick={onClick} aria-label={label} aria-pressed={active} data-tip={label}>
      <svg viewBox="0 0 24 38" width="22" height="34" aria-hidden="true">
        <path d="M5.5 3.5 Q12 0.8 18.5 3.5 L19.5 25 Q12 27.5 4.5 25 Z" fill={color} stroke="var(--line)" strokeWidth="1.5" strokeLinejoin="round" />
        <path d="M8 6 Q8.8 14 8.2 22" stroke={light ? "rgba(0,0,0,0.12)" : "rgba(255,255,255,0.42)"} strokeWidth="2.2" strokeLinecap="round" fill="none" />
        <path d="M5 24.5 Q12 27 19 24.5 L17 30.5 Q12 32 7 30.5 Z" fill={color} stroke="var(--line)" strokeWidth="1.4" strokeLinejoin="round" />
        <path d="M9 31 L12 36.5 L15 31" fill={color} stroke="var(--line)" strokeWidth="1.3" strokeLinejoin="round" />
        <path d="M4.8 22 Q12 24.2 19.2 22" stroke="var(--line)" strokeWidth="1" fill="none" opacity="0.5" />
      </svg>
    </button>
  );
}

export function Dot({ color, active, onClick, label, ring }: { color: string; active?: boolean; onClick?: () => void; label: string; ring?: boolean }) {
  return (
    <button type="button" className={"color-dot" + (active ? " active" : "") + (ring ? " ring" : "")} style={{ ["--c" as string]: color }} onClick={onClick} aria-label={label} aria-pressed={active} data-tip={label} />
  );
}

export function isLight(hex: string): boolean {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex);
  if (!m) return true;
  const n = parseInt(m[1], 16);
  return 0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255) > 170;
}

/** A custom colour: the native picker behind a dashed "+" swatch. */
export function CustomColor({ value, onChange }: { value: string; onChange: (c: string) => void }) {
  const id = useId();
  return (
    <label className="custom-color" htmlFor={id} data-tip="Any colour">
      <input id={id} type="color" value={/^#[0-9a-f]{6}$/i.test(value) ? value : "#2b211a"} onChange={(e) => onChange(e.target.value)} />
      <span aria-hidden="true">+</span>
    </label>
  );
}

/** Slider with a wavy pencil track and a diamond thumb. */
export function WavySlider({ value, min = 0, max = 1, step = 0.01, onChange, label, left, right, format }: { value: number; min?: number; max?: number; step?: number; onChange: (v: number) => void; label: string; left?: string; right?: string; format?: (v: number) => string }) {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div className="wavy">
      {left && <span className="wavy-end">{left}</span>}
      <div className="wavy-track" style={{ ["--pct" as string]: pct + "%" }}>
        <svg viewBox="0 0 120 12" preserveAspectRatio="none" aria-hidden="true">
          <path d="M1 6 Q 6 1 11 6 T 21 6 T 31 6 T 41 6 T 51 6 T 61 6 T 71 6 T 81 6 T 91 6 T 101 6 T 111 6 T 119 6" />
        </svg>
        <span className="wavy-thumb" aria-hidden="true" />
        <input type="range" min={min} max={max} step={step} value={value} aria-label={label} onChange={(e) => onChange(Number(e.target.value))} />
      </div>
      {right && <span className="wavy-end">{right}</span>}
      {format && <span className="wavy-val">{format(value)}</span>}
    </div>
  );
}

export function Stepper({ value, onChange, min = 1, max = 400, step = 1, label, format = (v: number) => String(Math.round(v)) }: { value: number; onChange: (v: number) => void; min?: number; max?: number; step?: number; label: string; format?: (v: number) => string }) {
  return (
    <div className="stepper" role="group" aria-label={label}>
      <button type="button" onClick={() => onChange(Math.max(min, value - step))} aria-label={"Smaller " + label}>−</button>
      <span>{format(value)}</span>
      <button type="button" onClick={() => onChange(Math.min(max, value + step))} aria-label={"Larger " + label}>+</button>
    </div>
  );
}

export function Section({ title, children, defaultOpen = true, id }: { title: string; children: ReactNode; defaultOpen?: boolean; id?: string }) {
  const key = "sepia.section." + (id ?? title);
  const [open, setOpen] = useState(() => {
    const v = localStorage.getItem(key);
    return v === null ? defaultOpen : v === "1";
  });
  return (
    <section className={"insp-section" + (open ? " open" : "")}>
      <button
        type="button"
        className="insp-head"
        onClick={() => {
          setOpen(!open);
          localStorage.setItem(key, open ? "0" : "1");
        }}
        aria-expanded={open}
      >
        <span>{title}</span>
        <svg viewBox="0 0 12 8" width="12" height="8" aria-hidden="true">
          <path d="M1 1.5 L6 6.5 L11 1.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && <div className="insp-body">{children}</div>}
      <svg className="wavy-rule" viewBox="0 0 200 6" preserveAspectRatio="none" aria-hidden="true">
        <path d="M0 3 Q 5 0 10 3 T 20 3 T 30 3 T 40 3 T 50 3 T 60 3 T 70 3 T 80 3 T 90 3 T 100 3 T 110 3 T 120 3 T 130 3 T 140 3 T 150 3 T 160 3 T 170 3 T 180 3 T 190 3 T 200 3" />
      </svg>
    </section>
  );
}

export function Row({ label, children }: { label?: string; children: ReactNode }) {
  return (
    <div className="insp-row">
      {label && <span className="insp-label">{label}</span>}
      <div className="insp-controls">{children}</div>
    </div>
  );
}

// ---------- popover ----------

export function Popover({ anchor, open, onClose, children, side = "bottom", className = "", align = "center" }: { anchor: HTMLElement | null; open: boolean; onClose: () => void; children: ReactNode; side?: "bottom" | "top" | "right" | "left"; className?: string; align?: "center" | "start" | "end" }) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  useLayoutEffect(() => {
    if (!open || !anchor || !ref.current) return;
    const place = () => {
      const a = anchor.getBoundingClientRect();
      const p = ref.current!.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      let left = 0;
      let top = 0;
      const gap = 10;
      if (side === "bottom" || side === "top") {
        left = align === "start" ? a.left : align === "end" ? a.right - p.width : a.left + a.width / 2 - p.width / 2;
        top = side === "bottom" ? a.bottom + gap : a.top - p.height - gap;
        if (top + p.height > vh - 8) top = a.top - p.height - gap;
        if (top < 8) top = a.bottom + gap;
      } else {
        top = align === "start" ? a.top : align === "end" ? a.bottom - p.height : a.top + a.height / 2 - p.height / 2;
        left = side === "right" ? a.right + gap : a.left - p.width - gap;
      }
      left = Math.max(8, Math.min(vw - p.width - 8, left));
      top = Math.max(8, Math.min(vh - p.height - 8, top));
      setPos({ left, top });
    };
    place();
    const ro = new ResizeObserver(place);
    ro.observe(ref.current);
    window.addEventListener("resize", place);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", place);
    };
  }, [open, anchor, side, align]);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (ref.current?.contains(t) || anchor?.contains(t)) return;
      closeRef.current();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeRef.current();
    };
    window.addEventListener("pointerdown", onDown, true);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("pointerdown", onDown, true);
      window.removeEventListener("keydown", onKey);
    };
  }, [open, anchor]);
  if (!open) return null;
  return createPortal(
    <div ref={ref} className={"popover " + className} style={{ left: pos?.left ?? -9999, top: pos?.top ?? -9999 }} data-canvas-ignore>
      <SketchOutline seed={"pop" + side} />
      {children}
    </div>,
    document.body
  );
}

// ---------- dialog ----------

export function Dialog({ open, onClose, title, children, wide, tape = "var(--caramel)" }: { open: boolean; onClose: () => void; title: string; children: ReactNode; wide?: boolean; tape?: string }) {
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeRef.current();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);
  if (!open) return null;
  return createPortal(
    <div className="dialog-scrim" onPointerDown={(e) => e.target === e.currentTarget && onClose()} data-canvas-ignore>
      <div className={"dialog" + (wide ? " wide" : "")} role="dialog" aria-modal="true" aria-label={title}>
        <SketchOutline seed={"dlg" + title} />
        <Tape color={tape} className="dialog-tape" />
        <div className="dialog-head">
          <h2>{title}</h2>
          <button type="button" className="dialog-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <div className="dialog-body">{children}</div>
      </div>
    </div>,
    document.body
  );
}

// ---------- toasts ----------

interface ToastItem {
  id: number;
  msg: string;
  kind: "info" | "error";
}
const ToastCtx = createContext<(msg: string, kind?: "info" | "error") => void>(() => undefined);

export function ToastHost({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const push = useCallback((msg: string, kind: "info" | "error" = "info") => {
    const id = Math.random();
    setItems((xs) => [...xs.slice(-2), { id, msg, kind }]);
    window.setTimeout(() => setItems((xs) => xs.filter((x) => x.id !== id)), kind === "error" ? 6000 : 3200);
  }, []);
  return (
    <ToastCtx.Provider value={push}>
      {children}
      {createPortal(
        <div className="toasts" aria-live="polite">
          {items.map((t) => (
            <div key={t.id} className={"toast toast-" + t.kind}>
              {t.msg}
            </div>
          ))}
        </div>,
        document.body
      )}
    </ToastCtx.Provider>
  );
}

export function useToast() {
  return useContext(ToastCtx);
}

export function Spinner({ label = "Loading" }: { label?: string }) {
  return (
    <div className="spinner" role="status" aria-label={label}>
      <svg viewBox="0 0 40 40" width="40" height="40" aria-hidden="true">
        <path d="M20 5 C 30 5 35 12 35 20 C 35 29 28 35 20 35 C 11 35 5 29 5 20 C 5 13 10 7 16 5.6" />
      </svg>
    </div>
  );
}
